import React from "react";
import { Link } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import SEOHead from "../../components/SEOHead";
import { Briefcase, CheckCircle2, ArrowLeft, ArrowRight } from "lucide-react";

const industrialPipeline = "/images/industrial-pipeline.jpeg";
const oilPlatforms = "/images/oil-platforms.jpeg";

export default function Procurement() {
  return (
    <div className="min-h-screen bg-gray-950">
      <SEOHead
        title="Procurement Services - Alkane Engineering Limited"
        description="Efficient procurement of equipment and materials with quality assurance and cost optimization. Professional procurement services in Nigeria."
        keywords="procurement services, equipment procurement, material procurement, supplier management"
        canonical="https://alkanesengineering.com/services/procurement"
      />
      <Header />

      {/* Hero Section */}
      <section
        className="relative overflow-hidden pt-16 pb-20 md:pt-24 md:pb-28"
        style={{
          backgroundImage: `url(${industrialPipeline})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-primary mb-6 hover:gap-3 transition-all"
          >
            <ArrowLeft size={20} /> Back to Services
          </Link>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 text-white">
            Procurement Services
          </h1>
          <p className="text-lg text-gray-100 max-w-2xl">
            Efficient equipment and materials sourcing with quality assurance
            and cost optimization.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section
        className="relative py-16 md:py-24"
        style={{
          backgroundImage: `url(${industrialPipeline})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gray-950/85"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="inline-flex p-4 rounded-xl bg-gradient-to-br from-purple-400 to-pink-400 text-white flex-shrink-0">
                  <Briefcase size={32} />
                </div>
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
                    Procurement Services
                  </h2>
                  <p className="text-gray-300">Efficient equipment sourcing</p>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-white mb-4">Overview</h3>
                <p className="text-lg text-gray-300 leading-relaxed">
                  We handle complete procurement management from vendor
                  qualification through delivery, ensuring timely sourcing of
                  high-quality equipment and materials. Our established supply
                  chain network guarantees competitive pricing without
                  compromising quality standards.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-white mb-4">
                  Key Capabilities
                </h3>
                <ul className="space-y-3">
                  {[
                    "Vendor identification and evaluation",
                    "Competitive bidding management",
                    "Quality assurance and inspection",
                    "Supply chain optimization",
                    "Customs clearance and logistics",
                    "Cost management and budget control",
                  ].map((capability, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-3 text-gray-300"
                    >
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span>{capability}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-white mb-4">
                  Sectors Served
                </h3>
                <div className="flex flex-wrap gap-2">
                  {["Oil & Gas", "Power Generation", "Infrastructure"].map(
                    (sector, idx) => (
                      <span
                        key={idx}
                        className="px-4 py-2 rounded-full bg-primary/20 text-primary font-semibold text-sm border border-primary/40"
                      >
                        {sector}
                      </span>
                    ),
                  )}
                </div>
              </div>
            </div>

            {/* Right Highlight */}
            <div className="bg-gray-800 rounded-2xl border-2 border-primary/30 p-8 h-fit">
              <div className="text-center space-y-6">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center mx-auto">
                  <Briefcase size={40} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white">
                  Need Equipment?
                </h3>
                <p className="text-gray-300">
                  Let us handle your procurement needs efficiently.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 w-full bg-primary text-white px-6 py-3 rounded-lg font-semibold transition-all hover:shadow-lg hover:shadow-primary/30 hover:-translate-y-1"
                >
                  Get Quote <ArrowRight size={20} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="relative text-white py-16 md:py-24"
        style={{
          backgroundImage: `url(${oilPlatforms})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-primary/85"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 className="text-3xl md:text-5xl font-bold text-white">
            Ready to Source Your Equipment?
          </h2>
          <p className="text-lg text-gray-100 max-w-2xl mx-auto">
            Contact us for competitive procurement solutions.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-primary px-8 py-4 rounded-lg font-semibold text-lg transition-all hover:shadow-xl hover:-translate-y-1 active:scale-95"
          >
            Request Procurement Support
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
